import React, { useState } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { AlternateButton } from '@ratehub/base-ui';

import fetchLatestBlogPosts from '../functions/fetchLatestBlogPosts';
import HeadingPropTypes from '../definitions/HeadingPropTypes';
import FactBlockContainer from './FactBlockContainer';
import FactBlock from './FactBlock';


const VARIANTS = {
    LIGHT: 'light',
    DARK: 'dark',
};

function LatestBlogPosts({
    heading,
    useAd,
    variant,
    initialPosts,
    initialPage,
    categories,
    tags,
    hasMorePages,
    include,
    ...otherProps
}) {
    const intl = useIntl();

    const [ postsToShow, setPostsToShow ] = useState(initialPosts);
    const [ currentPage, setCurrentPage ] = useState(initialPage);
    const [ canFetchMore, setCanFetchMore ] = useState(hasMorePages);
    const [ isFetching, setIsFetching ] = useState(false);

    async function handleRequestMorePages() {
        setIsFetching(true);

        try {
            const nextPosts = await fetchLatestBlogPosts({
                page: currentPage,
                perPageCount: 6,
                locale: intl.locale,
                categories: categories,
                tags: tags,
                include: include,
            });

            setPostsToShow(prevPosts => prevPosts.concat(nextPosts.posts));
            setCanFetchMore(currentPage != nextPosts.totalPages);
            setCurrentPage(prevCount => prevCount + 1);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(`Error requesting additional blog articles: ${error}`);
        }

        setIsFetching(false);
    }

    return (
        <Container
            {...otherProps}
        >
            <FactBlockContainer
                heading={heading}
                useAd={useAd}
                variant={variant}
            >
                <For
                    each="post"
                    of={postsToShow}
                >
                    <FactBlock
                        key={post.title}
                        isArticle
                        isImageAndTitleALink
                        {...post}
                    />
                </For>
            </FactBlockContainer>

            <If condition={canFetchMore}>
                <div className="cta-container rh-mt-1 rh-mx-auto rh-mb-5">
                    <AlternateButton
                        onClick={handleRequestMorePages}
                        disabled={isFetching}
                        message={isFetching
                            ? MESSAGES.BUTTON_FETCHING
                            : MESSAGES.BUTTON_READY
                        }
                    />
                </div>
            </If>
        </Container>
    );
}

LatestBlogPosts.propTypes = {
    heading: HeadingPropTypes,
    variant: PropTypes.oneOf(Object.values(VARIANTS)),
    useAd: PropTypes.bool,
    initialPosts: PropTypes.array.isRequired,
    initialPage: PropTypes.number,
    categories: PropTypes.number,
    tags: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]),
    hasMorePages: PropTypes.bool,
    include: PropTypes.string,
};

LatestBlogPosts.defaultProps = {
    heading: undefined,
    variant: VARIANTS.DARK,
    useAd: false,
    initialPage: 2,
    categories: undefined,
    tags: undefined,
    hasMorePages: true,
    include: undefined,
};

const Container = styled.section`
    > .cta-container {
        max-width: 24rem;
    }
`;

LatestBlogPosts.hasHeading = true;
LatestBlogPosts.requiresLayoutRow = true;

const MESSAGES = defineMessages({
    BUTTON_READY: {
        id: 'web-components.LatestBlogPosts.DefaultButtonText',
        defaultMessage: 'Show more',
    },
    BUTTON_FETCHING: {
        id: 'web-components.LatestBlogPosts.FetchingButtonText',
        defaultMessage: 'Fetching …',
    },
});

export default LatestBlogPosts;
